import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Sparkles,
  Bell,
  Film,
  Image as ImageIcon,
  Smartphone,
  CheckCircle2,
  Send,
  Loader2,
  Star,
  Handshake,
  Zap,
  Mic,
  Camera,
  TrendingUp,
  X,
  Heart,
  MessageCircle,
  ThumbsUp,
  Eye,
  Search,
  MapPin,
  BarChart3,
  PlayCircle,
  Layers,
  Bot,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import './NicheSocialLanding.css';

const API = process.env.REACT_APP_BACKEND_URL;

const INTEREST_OPTIONS = [
  { value: 'shake_up', label: 'Social Shake Up — consultancy day' },
  { value: 'studio_services', label: 'Studio Services (production + on-camera)' },
  { value: 'podcast', label: 'Podcast Management' },
  { value: 'tool', label: 'Social Week Tool (asset bolt-on)' },
  { value: 'beta', label: 'Social Week — DIY (Beta)' },
  { value: 'managed', label: 'Fully Managed Social' },
  { value: 'not_sure', label: "Not sure yet — show me everything" },
];

/* ─────────────────────────────────────────────────────────────
   HERO SOCIAL STAGE
   Animated phone with a faux Reel and floating social reactions.
   Pure CSS keyframes — no extra libraries, no JS timers.
   ───────────────────────────────────────────────────────────── */
function HeroSocialStage() {
  return (
    <div className="ns-hero-stage" data-testid="ns-hero-stage" aria-hidden="true">
      <div className="ns-stage-glow ns-stage-glow-1" />
      <div className="ns-stage-glow ns-stage-glow-2" />

      {/* Floating reactions */}
      <div className="ns-float ns-float-heart">
        <Heart size={16} fill="#ec4899" strokeWidth={0} />
        <span>+128</span>
      </div>
      <div className="ns-float ns-float-comment">
        <MessageCircle size={15} fill="#f6b545" strokeWidth={0} />
        <span>Lovely!</span>
      </div>
      <div className="ns-float ns-float-thumb">
        <ThumbsUp size={15} fill="#6aa9ff" strokeWidth={0} />
      </div>
      <div className="ns-float ns-float-views">
        <Eye size={14} />
        <span>12.4k</span>
      </div>
      <div className="ns-float ns-float-sparkle">
        <Sparkles size={14} />
      </div>

      {/* The phone */}
      <div className="ns-phone" data-testid="ns-hero-phone">
        <div className="ns-phone-notch" />
        <div className="ns-phone-screen">
          {/* Status bar */}
          <div className="ns-phone-status">
            <span>9:41</span>
            <span className="ns-phone-status-dots">
              <i /><i /><i />
            </span>
          </div>

          {/* Post header */}
          <div className="ns-post-header">
            <div className="ns-post-avatar">N</div>
            <div className="ns-post-meta">
              <div className="ns-post-name">nichecom.social</div>
              <div className="ns-post-time">Just now · Reel</div>
            </div>
          </div>

          {/* Carousel of "posts" cycling */}
          <div className="ns-post-stack">
            <div className="ns-post-card ns-post-card-1">
              <div className="ns-post-tag">FOR SALE</div>
              <div className="ns-post-house">
                <div className="ns-house-roof" />
                <div className="ns-house-body">
                  <div className="ns-house-door" />
                  <div className="ns-house-window" />
                  <div className="ns-house-window ns-house-window-2" />
                </div>
              </div>
              <div className="ns-post-price">£475,000</div>
            </div>
            <div className="ns-post-card ns-post-card-2">
              <div className="ns-post-tag ns-post-tag-amber">NEW LISTING</div>
              <div className="ns-post-bars">
                <div className="ns-bar" /><div className="ns-bar" />
                <div className="ns-bar" /><div className="ns-bar" />
              </div>
              <div className="ns-post-price ns-post-price-sm">3 bed · Wroughton</div>
            </div>
            <div className="ns-post-card ns-post-card-3">
              <div className="ns-post-tag ns-post-tag-pink">SOLD STC</div>
              <div className="ns-post-confetti">
                <span /><span /><span /><span /><span /><span />
              </div>
              <div className="ns-post-price">Sold in 9 days</div>
            </div>
          </div>

          {/* Actions */}
          <div className="ns-post-actions">
            <span className="ns-post-action"><Heart size={17} /></span>
            <span className="ns-post-action"><MessageCircle size={17} /></span>
            <span className="ns-post-action"><Send size={17} /></span>
          </div>
          <div className="ns-post-likes">1,248 likes</div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SHOWCASE CAROUSEL
   Five auto-rotating slides demoing what NicheSocial does:
   local algorithm lift, AI search, smart weekly planner,
   reels/stories/carousels, and live analytics.
   ───────────────────────────────────────────────────────────── */
const SHOWCASE_SLIDES = [
  {
    key: 'algo',
    eyebrow: 'Local algorithm lift',
    title: 'Get found in your patch again',
    body:
      'Weekly cadence + branch-tagged posts retrain the algorithm to push your content to local feeds. Real reach in the towns you actually sell in.',
    accent: '#f6b545',
  },
  {
    key: 'ai',
    eyebrow: 'AI search ready',
    title: 'Be the answer when buyers ask AI',
    body:
      'AI Overviews and ChatGPT search pull from active local social signals. We feed them so your branch is the one being recommended.',
    accent: '#a78bfa',
  },
  {
    key: 'calendar',
    eyebrow: 'Smart weekly planner',
    title: 'A whole week, planned in a click',
    body:
      'Drag listings, lifestyle and market posts onto the days that work. We fill in the captions, the schedule, the lot.',
    accent: '#34d399',
  },
  {
    key: 'formats',
    eyebrow: 'Reels · Stories · Carousels',
    title: 'Every format, ready out of the shoot',
    body:
      'One on-site visit becomes Reels, Stories, image carousels and posts — auto-cropped, branded, captioned and ready to publish.',
    accent: '#ec4899',
  },
  {
    key: 'analytics',
    eyebrow: 'Live analytics',
    title: 'See what landed and double down',
    body:
      'Branch-by-branch reach, follower growth and engagement reporting. The wins are obvious. The duds get cut.',
    accent: '#6aa9ff',
  },
];

function ShowcaseCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = SHOWCASE_SLIDES.length;

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % total);
    }, 5400);
    return () => clearInterval(id);
  }, [paused, total]);

  const go = (n) => setActive(((n % total) + total) % total);
  const slide = SHOWCASE_SLIDES[active];

  return (
    <section
      className="ns-section ns-section-showcase"
      data-testid="ns-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="ns-container">
        <span className="ns-eyebrow-dark">Inside NicheSocial</span>
        <h2 className="ns-h2">What the engine actually does</h2>
        <p className="ns-section-sub">
          Five things working in the background, on every property, every week.
        </p>

        <div
          className="ns-show-wrap"
          style={{ '--show-accent': slide.accent }}
          data-testid={`ns-showcase-slide-${slide.key}`}
        >
          <button
            type="button"
            className="ns-show-arrow ns-show-arrow-prev"
            onClick={() => go(active - 1)}
            aria-label="Previous slide"
            data-testid="ns-showcase-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            className="ns-show-arrow ns-show-arrow-next"
            onClick={() => go(active + 1)}
            aria-label="Next slide"
            data-testid="ns-showcase-next"
          >
            <ChevronRight size={18} />
          </button>

          <div className="ns-show-stage">
            <div className="ns-show-grid">
              <div className="ns-show-copy" key={`copy-${slide.key}`}>
                <span className="ns-show-eyebrow">{slide.eyebrow}</span>
                <h3 className="ns-show-title">{slide.title}</h3>
                <p className="ns-show-body">{slide.body}</p>
                <div className="ns-show-meta">
                  <span className="ns-show-step">
                    {String(active + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                  </span>
                </div>
              </div>

              <div className="ns-show-visual" key={`vis-${slide.key}`}>
                {slide.key === 'algo' && <AlgoVisual />}
                {slide.key === 'ai' && <AiSearchVisual />}
                {slide.key === 'calendar' && <CalendarVisual />}
                {slide.key === 'formats' && <FormatsVisual />}
                {slide.key === 'analytics' && <AnalyticsVisual />}
              </div>
            </div>

            <div className="ns-show-dots" role="tablist" aria-label="Showcase slides">
              {SHOWCASE_SLIDES.map((s, i) => (
                <button
                  key={s.key}
                  role="tab"
                  aria-selected={i === active}
                  aria-label={s.eyebrow}
                  className={`ns-show-dot ${i === active ? 'is-active' : ''}`}
                  onClick={() => go(i)}
                  data-testid={`ns-showcase-dot-${s.key}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Slide visuals ── pure CSS-driven animations ── */

function AlgoVisual() {
  return (
    <div className="ns-vis ns-vis-algo">
      <div className="ns-vis-map">
        <div className="ns-map-pin ns-map-pin-1"><MapPin size={14} /></div>
        <div className="ns-map-pin ns-map-pin-2"><MapPin size={14} /></div>
        <div className="ns-map-pin ns-map-pin-3"><MapPin size={14} /></div>
        <div className="ns-map-pulse ns-map-pulse-1" />
        <div className="ns-map-pulse ns-map-pulse-2" />
      </div>
      <div className="ns-vis-chart">
        <div className="ns-chart-head">
          <span>Local reach</span>
          <span className="ns-chart-up"><TrendingUp size={13} /> +312%</span>
        </div>
        <div className="ns-chart-bars">
          {[24, 38, 30, 56, 72, 88, 100].map((h, i) => (
            <div key={i} className="ns-chart-bar" style={{ '--h': `${h}%`, animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AiSearchVisual() {
  return (
    <div className="ns-vis ns-vis-ai">
      <div className="ns-ai-prompt">
        <Bot size={14} />
        <span className="ns-ai-typed">best estate agents in Wroughton</span>
        <span className="ns-ai-caret" />
      </div>
      <div className="ns-ai-result ns-ai-result-1">
        <div className="ns-ai-result-badge"><Sparkles size={11} /> AI pick</div>
        <div className="ns-ai-result-name">NicheCom Estates · Wroughton</div>
        <div className="ns-ai-result-meta"><Star size={11} fill="#f6b545" strokeWidth={0} /> 4.9 · 128 reviews</div>
      </div>
      <div className="ns-ai-result ns-ai-result-2">
        <div className="ns-ai-result-name">Local Property Co.</div>
        <div className="ns-ai-result-meta">4.6 · 84 reviews</div>
      </div>
      <div className="ns-ai-result ns-ai-result-3">
        <div className="ns-ai-result-name">High Street Homes</div>
        <div className="ns-ai-result-meta">4.4 · 52 reviews</div>
      </div>
    </div>
  );
}

function CalendarVisual() {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const fills = [
    { type: 'listing', label: 'New Listing' },
    { type: 'lifestyle', label: 'Local Tip' },
    { type: 'market', label: 'Market Stat' },
    { type: 'reel', label: 'Reel' },
    { type: 'sold', label: 'Sold STC' },
    { type: 'story', label: 'Story' },
    { type: 'carousel', label: 'Carousel' },
  ];
  return (
    <div className="ns-vis ns-vis-calendar">
      <div className="ns-cal-head">
        <Calendar size={14} /> This week
        <span className="ns-cal-status"><span className="ns-cal-dot" /> auto-planning</span>
      </div>
      <div className="ns-cal-grid">
        {days.map((day, i) => (
          <div key={day} className="ns-cal-col">
            <div className="ns-cal-day">{day}</div>
            <div
              className={`ns-cal-cell ns-cal-cell-${fills[i].type}`}
              style={{ animationDelay: `${i * 220}ms` }}
            >
              <span className="ns-cal-cell-tag">{fills[i].label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormatsVisual() {
  return (
    <div className="ns-vis ns-vis-formats">
      <div className="ns-fmt ns-fmt-reel">
        <div className="ns-fmt-tag"><PlayCircle size={12} /> REEL</div>
        <div className="ns-fmt-body ns-fmt-body-reel">
          <div className="ns-fmt-play" />
          <div className="ns-fmt-progress"><span /></div>
        </div>
      </div>
      <div className="ns-fmt ns-fmt-story">
        <div className="ns-fmt-story-ring"><div className="ns-fmt-story-ring-inner" /></div>
        <div className="ns-fmt-tag ns-fmt-tag-story">STORY</div>
      </div>
      <div className="ns-fmt ns-fmt-carousel">
        <div className="ns-fmt-tag"><Layers size={12} /> CAROUSEL</div>
        <div className="ns-fmt-cards">
          <span /><span /><span /><span />
        </div>
        <div className="ns-fmt-dots">
          <i className="is-active" /><i /><i /><i />
        </div>
      </div>
      <div className="ns-fmt-float ns-fmt-float-heart"><Heart size={14} fill="#ec4899" strokeWidth={0} /></div>
      <div className="ns-fmt-float ns-fmt-float-comment"><MessageCircle size={13} fill="#6aa9ff" strokeWidth={0} /></div>
      <div className="ns-fmt-float ns-fmt-float-thumb"><ThumbsUp size={13} fill="#f6b545" strokeWidth={0} /></div>
    </div>
  );
}

function AnalyticsVisual() {
  return (
    <div className="ns-vis ns-vis-analytics">
      <div className="ns-an-head">
        <BarChart3 size={14} /> Last 30 days
      </div>
      <div className="ns-an-metrics">
        <div className="ns-an-card">
          <div className="ns-an-label">Reach</div>
          <div className="ns-an-num">48,200</div>
          <div className="ns-an-up"><TrendingUp size={11} /> +212%</div>
        </div>
        <div className="ns-an-card">
          <div className="ns-an-label">Followers</div>
          <div className="ns-an-num">+418</div>
          <div className="ns-an-up"><TrendingUp size={11} /> +38%</div>
        </div>
        <div className="ns-an-card">
          <div className="ns-an-label">Engagement</div>
          <div className="ns-an-num">7.4%</div>
          <div className="ns-an-up"><TrendingUp size={11} /> +1.9pp</div>
        </div>
      </div>
      <div className="ns-an-line">
        <svg viewBox="0 0 200 60" preserveAspectRatio="none" className="ns-an-svg">
          <defs>
            <linearGradient id="ns-an-grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(246, 181, 69, 0.45)" />
              <stop offset="100%" stopColor="rgba(246, 181, 69, 0)" />
            </linearGradient>
          </defs>
          <path d="M0,48 L30,40 L55,42 L80,30 L110,22 L140,18 L170,10 L200,5 L200,60 L0,60 Z" fill="url(#ns-an-grad)" />
          <path d="M0,48 L30,40 L55,42 L80,30 L110,22 L140,18 L170,10 L200,5"
                fill="none" stroke="#f6b545" strokeWidth="2"
                pathLength="1" strokeDasharray="1" strokeDashoffset="1"
                style={{ animation: 'ns-line-draw 1.6s ease-out forwards' }} />
        </svg>
      </div>
    </div>
  );
}



export default function NicheSocialLanding({ onBack, onLogin }) {
  const enquireRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    agency_name: '',
    phone: '',
    interest: 'not_sure',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [shakeInfoOpen, setShakeInfoOpen] = useState(false);

  const scrollToEnquire = (preselectInterest) => {
    if (preselectInterest) {
      setForm((prev) => ({ ...prev, interest: preselectInterest }));
    }
    setShakeInfoOpen(false);
    enquireRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please add your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/enquiries/create`, {
        ...form,
        source: 'nichesocial_landing',
      });
      setSubmitted(true);
      toast.success('Thanks — we’ll be in touch shortly.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ns-page" data-testid="nichesocial-landing">
      {/* ───────────────────────── NAV ───────────────────────── */}
      <header className="ns-nav" data-testid="ns-nav">
        <button className="ns-back" onClick={onBack} data-testid="ns-back-btn">
          <ArrowLeft size={15} /> NicheCom
        </button>
        <span className="ns-wordmark" data-testid="ns-wordmark">
          <span className="ns-wordmark-niche">niche</span>
          <span className="ns-wordmark-social">social</span>
        </span>
        <div className="ns-nav-actions">
          {onLogin && (
            <button className="ns-nav-login" onClick={onLogin} data-testid="ns-login-btn">
              Log in
            </button>
          )}
          <button className="ns-nav-cta" onClick={scrollToEnquire} data-testid="ns-nav-cta">
            Enquire <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* ───────────────────────── HERO ───────────────────────── */}
      <section className="ns-hero" data-testid="ns-hero">
        <div className="ns-hero-orb ns-hero-orb-1" aria-hidden="true" />
        <div className="ns-hero-orb ns-hero-orb-2" aria-hidden="true" />
        <div className="ns-hero-grain" aria-hidden="true" />

        <div className="ns-hero-inner">
          <div className="ns-hero-content">
            <span className="ns-eyebrow" data-testid="ns-hero-eyebrow">
              <Sparkles size={13} /> Social media sorted.
            </span>
            <h1 className="ns-h1" data-testid="ns-hero-title">
              <span className="ns-h1-line">7 days of social.</span>
              <span className="ns-h1-accent">One click.</span>
            </h1>
            <p className="ns-hero-sub" data-testid="ns-hero-sub">
              Done-for-you social media for estate agents. We capture the assets on site.
              You publish them with confidence — or we run the whole week on your behalf.
            </p>
            <div className="ns-hero-ctas">
              <button className="ns-btn ns-btn-primary" onClick={scrollToEnquire} data-testid="ns-hero-enquire">
                Enquire about NicheSocial <ArrowRight size={15} />
              </button>
              <a className="ns-btn ns-btn-ghost" href="#how" data-testid="ns-hero-how">
                See how it works
              </a>
            </div>

            <div className="ns-hero-strip" data-testid="ns-hero-strip">
              <div className="ns-strip-pill"><CheckCircle2 size={13} /> Assets shot by NicheCom</div>
              <div className="ns-strip-pill"><CheckCircle2 size={13} /> AI captions, branch tone</div>
              <div className="ns-strip-pill"><CheckCircle2 size={13} /> DIY or fully managed</div>
            </div>
          </div>

          <HeroSocialStage />
        </div>
      </section>

      {/* ───────────────────── SERVICES (3 CARDS) ───────────────────── */}
      <section className="ns-section ns-section-dark" data-testid="ns-paths">
        <div className="ns-container">
          <span className="ns-eyebrow-dark">Pick what fits</span>
          <h2 className="ns-h2">Three ways to work with NicheSocial</h2>
          <p className="ns-section-sub">
            Start with a one-day consultancy or jump straight into an ongoing service —
            same content engine, three different gears.
          </p>

          <div className="ns-paths-grid">
            <article className="ns-path-card ns-path-card-shake" data-testid="ns-path-shake-up">
              <span className="ns-path-flag ns-path-flag-shake">Consultancy day</span>
              <div className="ns-path-icon ns-path-icon-shake">
                <Zap size={20} />
              </div>
              <h3 className="ns-path-title">Social Shake Up</h3>
              <p className="ns-path-tag">The Social Reset — one day, on-site</p>
              <p className="ns-path-body">
                We reignite your social channels so the algorithm and AI search
                actually find you again. Audit, a full month's worth of content sprinted
                from existing assets, plus a roadmap to keep it running.
              </p>
              <ul className="ns-path-list">
                <li><CheckCircle2 size={13} /> Channel audit + AI plan</li>
                <li><CheckCircle2 size={13} /> A month of social content in a day</li>
                <li><CheckCircle2 size={13} /> 30 days of Social Week — free</li>
              </ul>
              <div className="ns-path-actions">
                <button
                  type="button"
                  className="ns-path-more-info"
                  onClick={() => setShakeInfoOpen(true)}
                  data-testid="ns-path-shake-more-info"
                >
                  More info <ArrowRight size={13} />
                </button>
              </div>
            </article>

            <article className="ns-path-card ns-path-card-highlight" data-testid="ns-path-beta">
              <span className="ns-path-flag">Ongoing — most popular</span>
              <div className="ns-path-icon ns-path-icon-amber">
                <Calendar size={20} />
              </div>
              <h3 className="ns-path-title">Social Week — DIY <span className="ns-beta">Beta</span></h3>
              <p className="ns-path-tag">You drive — we power the engine</p>
              <p className="ns-path-body">
                The full Social Week planner. Drag your week into shape, mix listings
                with lifestyle and market posts, and let AI captions match your
                branch tone. Push reminders nudge your agents when it's time to publish.
              </p>
              <ul className="ns-path-list">
                <li><CheckCircle2 size={13} /> Weekly planner across every branch</li>
                <li><CheckCircle2 size={13} /> AI captions in your Social Voice</li>
                <li><CheckCircle2 size={13} /> Push reminders to the agent's phone</li>
              </ul>
            </article>

            <article className="ns-path-card" data-testid="ns-path-managed">
              <span className="ns-path-flag ns-path-flag-managed">Social sorted</span>
              <div className="ns-path-icon ns-path-icon-gold">
                <Handshake size={20} />
              </div>
              <h3 className="ns-path-title">Fully Managed</h3>
              <p className="ns-path-tag">Done for you</p>
              <p className="ns-path-body">
                Our team runs your Social Week end-to-end. We plan, caption and
                publish every post using the assets we've already captured for you.
                You get the results without ever opening the app.
              </p>
              <ul className="ns-path-list">
                <li><CheckCircle2 size={13} /> Dedicated social manager</li>
                <li><CheckCircle2 size={13} /> Weekly plan signed off with you</li>
                <li><CheckCircle2 size={13} /> Reporting on what landed and what didn't</li>
              </ul>
            </article>
          </div>
        </div>

        {/* Other services — Studio + Podcast */}
        <div className="ns-other-services" data-testid="ns-other-services">
          <div className="ns-other-services-head">
            <span className="ns-eyebrow-dark">Also from NicheSocial</span>
            <h3 className="ns-other-services-title">Other services we run</h3>
            <p className="ns-other-services-sub">
              Separate offerings — request as part of your enquiry or get in touch.
            </p>
          </div>
          <div className="ns-other-services-grid">
            <button
              type="button"
              className="ns-other-card"
              onClick={() => scrollToEnquire('studio_services')}
              data-testid="ns-other-studio"
            >
              <div className="ns-other-icon ns-other-icon-violet">
                <Film size={20} />
              </div>
              <h4>Studio Services</h4>
              <p>
                Polished pieces shot in our studio — talking heads, interviews, branded
                reels. <strong>On-camera confidence coaching</strong> included to get your
                agents performing on camera.
              </p>
              <span className="ns-other-cta">Enquire <ArrowRight size={13} /></span>
            </button>
            <button
              type="button"
              className="ns-other-card"
              onClick={() => scrollToEnquire('podcast')}
              data-testid="ns-other-podcast"
            >
              <div className="ns-other-icon ns-other-icon-cyan">
                <Mic size={20} />
              </div>
              <h4>Podcast Management</h4>
              <p>
                From "we should start one" to fully produced, edited and distributed. We
                handle the recording, the post-production and the publishing — you turn up
                and talk.
              </p>
              <span className="ns-other-cta">Enquire <ArrowRight size={13} /></span>
            </button>
          </div>
        </div>
      </section>

      <ShowcaseCarousel />

      {/* ───────────────────── HOW IT WORKS ───────────────────── */}
      <section className="ns-section ns-section-darker" id="how" data-testid="ns-how">
        <div className="ns-container">
          <span className="ns-eyebrow-dark">How it works</span>
          <h2 className="ns-h2">From shoot to scroll-stopping post — in one flow</h2>

          <div className="ns-steps">
            <div className="ns-step" data-testid="ns-step-1">
              <span className="ns-step-num">01</span>
              <h3>We shoot the property</h3>
              <p>Same NicheCom visit you already know. Photography, video and lifestyle assets in one go.</p>
            </div>
            <div className="ns-step-arrow" aria-hidden="true"><ArrowRight size={18} /></div>
            <div className="ns-step" data-testid="ns-step-2">
              <span className="ns-step-num">02</span>
              <h3>Assets generated</h3>
              <p>Sneak peeks, branded social images, lifestyle stills and AI captions tuned to your voice.</p>
            </div>
            <div className="ns-step-arrow" aria-hidden="true"><ArrowRight size={18} /></div>
            <div className="ns-step" data-testid="ns-step-3">
              <span className="ns-step-num">03</span>
              <h3>Plan your week</h3>
              <p>You drag posts onto days — or we do it for you on a fully managed retainer.</p>
            </div>
            <div className="ns-step-arrow" aria-hidden="true"><ArrowRight size={18} /></div>
            <div className="ns-step" data-testid="ns-step-4">
              <span className="ns-step-num">04</span>
              <h3>Publish &amp; track</h3>
              <p>Push reminders fire to the right agent. Posts go out. Reporting comes back.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── HIGHLIGHTS ───────────────────── */}
      <section className="ns-section ns-section-dark" data-testid="ns-highlights">
        <div className="ns-container">
          <span className="ns-eyebrow-dark">What you get</span>
          <h2 className="ns-h2">A complete social engine for your branch</h2>
          <div className="ns-highlight-grid">
            <div className="ns-highlight" data-testid="ns-hl-video">
              <Film size={18} />
              <h4>Videos direct in-app</h4>
              <p>Every clip we shoot lands inside NicheSocial — no WeTransfer link hunting.</p>
            </div>
            <div className="ns-highlight" data-testid="ns-hl-image">
              <ImageIcon size={18} />
              <h4>Branded images</h4>
              <p>Property and lifestyle imagery rebuilt to your brand for every format.</p>
            </div>
            <div className="ns-highlight" data-testid="ns-hl-caption">
              <Sparkles size={18} />
              <h4>AI captions in your tone</h4>
              <p>Social Voice settings keep emoji, spacing and personality on-brand.</p>
            </div>
            <div className="ns-highlight" data-testid="ns-hl-reminder">
              <Bell size={18} />
              <h4>Push reminders</h4>
              <p>Right agent, right post, right time. Nothing slips through the cracks.</p>
            </div>
            <div className="ns-highlight" data-testid="ns-hl-mobile">
              <Smartphone size={18} />
              <h4>Plan desktop, post mobile</h4>
              <p>Installable PWA — feels native, works anywhere.</p>
            </div>
            <div className="ns-highlight" data-testid="ns-hl-quality">
              <Star size={18} />
              <h4>Studio-grade quality</h4>
              <p>You’re publishing professional content, not phone snaps.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── ENQUIRY FORM ───────────────────── */}
      <section className="ns-section ns-section-enquire" ref={enquireRef} data-testid="ns-enquire">
        <div className="ns-container ns-enquire-container">
          <div className="ns-enquire-copy">
            <span className="ns-eyebrow-dark">Enquire</span>
            <h2 className="ns-h2 ns-h2-tight">Want this for your agency?</h2>
            <p className="ns-section-sub">
              Tell us a little about your business and which level you’re curious about.
              We’ll reply within one working day with a tailored plan and pricing.
            </p>
            <ul className="ns-enquire-points">
              <li><CheckCircle2 size={14} /> No commitment — just a conversation</li>
              <li><CheckCircle2 size={14} /> Works with your existing NicheCom shoots</li>
              <li><CheckCircle2 size={14} /> Move between DIY and managed any time</li>
            </ul>
          </div>

          <form className="ns-form" onSubmit={handleSubmit} data-testid="ns-form">
            {submitted ? (
              <div className="ns-form-success" data-testid="ns-form-success">
                <CheckCircle2 size={28} />
                <h3>Thanks — we’ve got it.</h3>
                <p>
                  Your enquiry is in. The NicheCom team will be in touch on{' '}
                  <strong>{form.email}</strong> within one working day.
                </p>
                <button
                  type="button"
                  className="ns-btn ns-btn-ghost"
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', agency_name: '', phone: '', interest: 'not_sure', message: '' }); }}
                  data-testid="ns-form-new"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <>
                <div className="ns-form-row">
                  <label className="ns-field">
                    <span>Your name <em>*</em></span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={handleChange('name')}
                      required
                      autoComplete="name"
                      placeholder="Jane Smith"
                      data-testid="ns-input-name"
                    />
                  </label>
                  <label className="ns-field">
                    <span>Email <em>*</em></span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={handleChange('email')}
                      required
                      autoComplete="email"
                      placeholder="you@agency.co.uk"
                      data-testid="ns-input-email"
                    />
                  </label>
                </div>

                <div className="ns-form-row">
                  <label className="ns-field">
                    <span>Agency name</span>
                    <input
                      type="text"
                      value={form.agency_name}
                      onChange={handleChange('agency_name')}
                      placeholder="Bridges Estate Agents"
                      data-testid="ns-input-agency"
                    />
                  </label>
                  <label className="ns-field">
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={handleChange('phone')}
                      placeholder="07000 000000"
                      autoComplete="tel"
                      data-testid="ns-input-phone"
                    />
                  </label>
                </div>

                <label className="ns-field">
                  <span>I’m interested in</span>
                  <div className="ns-radio-grid" role="radiogroup" data-testid="ns-radio-grid">
                    {INTEREST_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className={`ns-radio ${form.interest === opt.value ? 'ns-radio-active' : ''}`}
                        data-testid={`ns-radio-${opt.value}`}
                      >
                        <input
                          type="radio"
                          name="interest"
                          value={opt.value}
                          checked={form.interest === opt.value}
                          onChange={handleChange('interest')}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </label>

                <label className="ns-field">
                  <span>Anything else? (optional)</span>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="Tell us about your branches, current social workflow, anything we should know…"
                    data-testid="ns-input-message"
                  />
                </label>

                <button
                  type="submit"
                  className="ns-btn ns-btn-primary ns-btn-block"
                  disabled={submitting}
                  data-testid="ns-form-submit"
                >
                  {submitting ? (
                    <><Loader2 size={15} className="ns-spin" /> Sending…</>
                  ) : (
                    <>Send enquiry <Send size={14} /></>
                  )}
                </button>
                <p className="ns-form-fineprint">
                  We’ll only use this to get in touch about NicheSocial. No spam, ever.
                </p>
              </>
            )}
          </form>
        </div>
      </section>

      {/* ───────────────────────── FOOTER ───────────────────────── */}
      <footer className="ns-footer" data-testid="ns-footer">
        <div className="ns-footer-inner">
          <span className="ns-wordmark ns-wordmark-footer">
            <span className="ns-wordmark-niche">niche</span>
            <span className="ns-wordmark-social">social</span>
          </span>
          <p className="ns-footer-note">
            Powered by NicheCom property marketing assets. <br />
            Estate agent social, sorted.
          </p>
          <button className="ns-footer-back" onClick={onBack} data-testid="ns-footer-back">
            Back to NicheCom <ArrowRight size={13} />
          </button>
        </div>
      </footer>

      {/* ──────────── SOCIAL SHAKE UP — More info modal ──────────── */}
      {shakeInfoOpen && (
        <div
          className="ns-modal-backdrop"
          onClick={() => setShakeInfoOpen(false)}
          data-testid="ns-shake-modal-backdrop"
        >
          <div
            className="ns-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="ns-shake-modal"
            role="dialog"
            aria-modal="true"
          >
            <button
              className="ns-modal-close"
              onClick={() => setShakeInfoOpen(false)}
              data-testid="ns-shake-modal-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <span className="ns-shake-flag" data-testid="ns-modal-flag">
              <Zap size={12} /> Consultancy day
            </span>
            <h3 className="ns-modal-title">Social Shake Up <span className="ns-modal-subtitle">— The Social Reset</span></h3>
            <p className="ns-modal-lede">
              One day, on-site. We reignite your social channels so the algorithm
              and the new wave of AI search actually find you again.
            </p>
            <p className="ns-modal-body">
              Local social is back. Estate agents who feed Instagram, TikTok and Facebook
              with real branch content win the AI Overview slots and the "near me"
              results. We come in for a day, audit what you're doing, then build
              <strong> a full month's worth of social content</strong> from the assets we
              already have at source — more usable material in one visit than your team
              could produce on their own in weeks. You leave with the plan and the
              sustainable methods to keep it running.
            </p>

            <h4 className="ns-modal-section-title">What you get on the day</h4>
            <div className="ns-shake-grid ns-shake-grid-modal">
              <div className="ns-shake-item">
                <TrendingUp size={18} />
                <h4>Channel audit + AI plan</h4>
                <p>Where you're losing reach and how to fix it for the algorithm <em>and</em> AI search.</p>
              </div>
              <div className="ns-shake-item">
                <Camera size={18} />
                <h4>A month of social content</h4>
                <p>Sprinted from existing NicheCom assets — more usable content from one visit than your team manages in weeks.</p>
              </div>
              <div className="ns-shake-item">
                <CheckCircle2 size={18} />
                <h4>Roadmap &amp; strategy</h4>
                <p>Written takeaway with sustainable methods to keep the momentum going after we leave.</p>
              </div>
              <div className="ns-shake-item ns-shake-item-bonus">
                <Sparkles size={18} />
                <h4>30 days of Social Week — free</h4>
                <p>Try the planner on us. See first-hand which ongoing tier fits before committing.</p>
              </div>
            </div>

            <div className="ns-modal-cta-row">
              <button
                className="ns-btn ns-btn-primary"
                onClick={() => scrollToEnquire('shake_up')}
                data-testid="ns-shake-modal-enquire"
              >
                Enquire about Social Shake Up <ArrowRight size={15} />
              </button>
              <span className="ns-shake-poa">Pricing on application</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
